import SmallTitle from "@/components/mini-components/SmallTitle";
import PageHead from "@/components/PageHead";
import { useI18n } from "@/i18n/I18nProvider";
import { api } from "@/utils/Axios";
import { colors } from "@/utils/const";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { arFont, enFont } from "@/styles/font";
import OfferForm from "@/components/application/offerForm";
import ShareSocial from "@/components/share-social";
import GenericSwiper from "@/components/general-swiper";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import Head from "next/head";
import { OfferStep } from "@/components/products-revamp/offer-step";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CenterTitle from "@/components/center-title";
import { getOffersText } from "../../../locales/offers";
const OFFERS_PER_PAGE = 12;
const PRODUCT_ID = "6";

function getOffersCacheKey(lang) {
  return `offers_cache_product_${PRODUCT_ID}_${lang}`;
}

function readOffersFromSession(lang) {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(getOffersCacheKey(lang));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveOffersToSession(lang, offers) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(getOffersCacheKey(lang), JSON.stringify(offers));
  } catch {}
}

function readPageFromSession(lang) {
  if (typeof window === "undefined") return 1;
  const raw = sessionStorage.getItem(`${getOffersCacheKey(lang)}_page`);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function savePageToSession(lang, page) {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(`${getOffersCacheKey(lang)}_page`, String(page));
}

/*
 * The hero is a CSS background-image, so the browser cannot discover it until
 * the stylesheet is parsed and the section laid out — it was the measured LCP
 * element on this page. Preloading makes it start with the document instead.
 * Kept as a constant so the <link> and the sx rule can never drift apart.
 */
const OFFERS_HERO_IMAGE =
  "https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/banner-ar.png";

const STEPS_BREAKPOINTS = {
  0: { slidesPerView: 1 },
  600: { slidesPerView: 2 }, // sm
  900: { slidesPerView: "auto" }, // md
};

/*
 * Was declared inside ProductsOffers, so every parent render produced a new
 * component type and React tore down and remounted the whole carousel
 * (re-initialising Swiper) whenever unrelated state such as the offer dialog
 * or the current page changed. Module scope gives it a stable identity.
 */
function OffersStepsSection({ t, locale, isSmall, isArabic, steps }) {
  const offersText = getOffersText(locale);
  return (
    <>
      <CenterTitle
        title={offersText.offers_steps?.title}
        subtitle={offersText.offers_steps?.subtitle}
        color="#2D317F"
        title_variant="h2"
        subtitle_variant="h3"
      />
      <GenericSwiper
        {...(!isSmall ? { shrink: true } : {})}
        showNavigation={false}
        spaceBetween={40}
        autoplayDelay={4000}
        locale={locale}
        breakpoints={STEPS_BREAKPOINTS}
        type="details"
        hasArrows={true}
        arrowColor="#2D317F"
        sx={{
          "& .swiper": {
            width: "100%",
            direction: locale == "ar" ? "rtl" : "ltr",
            mt: { xs: "-80px", md: 0 },
          },
          "& .swiper-slide": {
            width: { md: 520 },
          },
          "& .swiper-button-prev, & .swiper-button-next": {
            display: "none !important",
          },
          padding: 0,
        }}
        items={steps}
        CardComponent={({ stepNumber, content, img }) => (
          <OfferStep
            stepNumber={stepNumber}
            content={content}
            img={img}
            isArabic={isArabic}
          />
        )}
        withSpace
      />
    </>
  );
}

export default function ProductsOffers() {
  const { t, locale } = useI18n();
  const offersText = getOffersText(locale);

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [page, setPage] = useState(1);

  const lang = locale === "en" ? "en" : "ar";
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isArabic = locale === "ar";

  const stepsWithNumber = useMemo(
    () =>
      offersText.offers_steps.steps.map((step, idx) => ({
        ...step,
        stepNumber: idx + 1,
      })),
    [offersText],
  );
  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    // 1) read cache first
    const cachedOffers = readOffersFromSession(lang);
    const cachedPage = readPageFromSession(lang);

    if (cachedOffers && cachedOffers.length) {
      setOffers(cachedOffers);
      setPage(cachedPage);
      setLoading(false);
      return;
    }

    // 2) fetch if no cache
    api
      .post(`/services/offers?lang=${lang}`, {
        productId: PRODUCT_ID,
        language: lang,
        merchantId: "0",
      })
      .then((response) => {
        if (cancelled) return;

        const mappedOffers = (response.data || []).map((offer) => ({
          id: offer.id,
          img: offer.merchantImage,
          title: offer.Title,
          role: offer.Description,
          expired: offer.expiryDate,
        }));

        setOffers(mappedOffers);
        saveOffersToSession(lang, mappedOffers);

        // reset page
        setPage(1);
        savePageToSession(lang, 1);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load offers");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const totalPages = useMemo(() => {
    return Math.ceil((offers?.length || 0) / OFFERS_PER_PAGE);
  }, [offers]);

  const paginatedOffers = useMemo(() => {
    return offers.slice((page - 1) * OFFERS_PER_PAGE, page * OFFERS_PER_PAGE);
  }, [offers, page]);

  useEffect(() => {
    if (!totalPages) return;
    if (page > totalPages) {
      setPage(1);
      savePageToSession(lang, 1);
    }
  }, [totalPages, page, lang]);

  const handleOpen = (offer) => {
    setSelectedOffer(offer);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOffer(null);
  };

  return (
    <>
      <PageHead
        title={offersText.main_tags.title}
        description={offersText.main_tags.meta_desc}
        scripts={offersText.main_tags.scripts}
      />
      <Head>
        <link
          rel="preload"
          as="image"
          href={OFFERS_HERO_IMAGE}
          fetchpriority="high"
        />
      </Head>
      {/*Start Hero Section */}
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 589,
          backgroundImage: `url(${OFFERS_HERO_IMAGE})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        {/* Gradient layer */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: "linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.70) 100%)",
              md: "linear-gradient(90deg, rgba(0,0,0,0) 0.01%, rgba(17,18,48,0.38) 36.54%, rgba(26,29,74,0.58) 58.17%, rgba(37,41,106,0.83) 83.17%, #2D317F 99.99%)",
            },
            zIndex: 1,
          }}
        />

        {/*  Purple diagonal shape  */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: isArabic ? 0 : "auto",
            left: isArabic ? "auto" : 0,

            height: "100%",
            clipPath: isArabic
              ? "polygon(90% 0%, 100% 0%, 100% 100%, 5% 100%)"
              : "polygon(5% 0%, 100% 0%, 100% 100%, 90% 100%)",
            width: { xs: "100%", md: "30%" },
            backgroundColor: "rgba(149, 123, 255, 0.40)",
            zIndex: 2,
            pointerEvents: "none",
            transform: isArabic ? "none" : "rotate(180deg)",
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: "absolute",
            zIndex: 3,
            top: "50%",
            transform: "translate(0, -50%)",
          }}
          className="horizontal-safe-padding"
        >
          <Box sx={{ minWidth: 0, maxWidth: 550 }}>
            <Box
              component="img"
              src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/logo.svg"
              alt="logo"
              sx={{
                width: 160,
                height: "auto",
                display: "block",
              }}
            />

            <Box sx={{ mt: 3 }}>
              <SmallTitle
                h2="h1"
                title={offersText.hero_section?.title}
                color="white"
              />
            </Box>

            <Typography
              sx={{
                fontSize: "16px",
                mt: 3,
                mb: 1,
                color: "white",
              }}
            >
              {offersText.hero_section?.subtitle1}
            </Typography>

            <Typography sx={{ fontSize: "16px", mb: 3, color: "white" }}>
              {offersText.hero_section?.subtitle2}
            </Typography>

            <Button
              component="a"
              href="https://play.google.com/store/apps/details?id=eg.contact"
              // target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                borderRadius: 2,
                padding: {
                  xs: "12px 80px",
                  md: "12px 150px",
                  lg: "12px 200px",
                },
                bgcolor: colors.orange,
                boxShadow:
                  "0 2px 10px 0 rgba(4, 9, 33, 0.05), 0 2px 0 0 rgba(4, 9, 33, 0.05), 0 0 5px 0 rgba(4, 9, 33, 0.03)",
                "&:hover": { bgcolor: colors.orange, boxShadow: "inherit" },
              }}
            >
              {offersText.hero_section?.btn_download}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Start Latest Offers */}
      <Box sx={{ bgcolor: colors.light_baby_white }}>
        {loading && (
          <div className="box-loading">
            <span className="loader"></span>
          </div>
        )}
        {!loading && !error && (
          <Box className="horizontal-safe-padding vertical-safe-padding">
            <Box sx={{ pb: 3 }}>
              <SmallTitle title={t.last_offers} h2="h2" />

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {paginatedOffers.map((offer) => (
                  <Grid item key={offer.id} xs={12} md={4} lg={3}>
                    <Box
                      onClick={() => handleOpen(offer)}
                      sx={{
                        padding: "20px 24px",
                        borderRadius: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        bgcolor: "#fff",
                        cursor: "pointer",
                        transition: "0.2s",
                        "&:hover": {
                          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        alt={offer?.merchantNameEn}
                        src={offer.img}
                        sx={{
                          width: "100%",
                          height: "135px",
                          borderRadius: "7px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "24px",
                          color: "#252C32",
                          fontWeight: "700",
                          lineHeight: "36px",
                        }}
                      >
                        {offer?.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: colors.navy_blue,
                        }}
                      >
                        {offer?.role}
                      </Typography>
                      <Typography
                        sx={{
                          bgcolor: "#F8FAFC",
                          padding: "10px",
                          borderRadius: "8px",
                          textAlign: "center",
                          fontSize: "14px",
                          color: "#686B7A",
                        }}
                      >
                        {offersText.valid_until}
                        {offer.expired?.split(" ")[0].replaceAll("-", "/")}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
                {offers?.length == 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Box
                      component="img"
                      src="/no-data.svg"
                      sx={{
                        width: { xs: "250px", md: "400px" },
                        height: { xs: "250px", md: "400px" },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colors.orange,
                        mt: 1,
                      }}
                    >
                      {offersText.no_data}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        slots={{
                          previous:
                            locale === "ar"
                              ? ChevronRightIcon
                              : ChevronLeftIcon,
                          next:
                            locale === "ar"
                              ? ChevronLeftIcon
                              : ChevronRightIcon,
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            </Box>

            {/* Dialog */}
            <Dialog
              open={open}
              onClose={handleClose}
              fullWidth
              maxWidth="lg"
              sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  overflow: "hidden",
                  maxHeight: "90vh",
                },
              }}
            >
              <DialogContent
                sx={{
                  m: { md: "24px 32px" },
                  p: { xs: 2, md: "32px 24px" },
                  bgcolor: "#F8FAFC",
                  borderRadius: "12px",
                  height: "auto",
                }}
                className={
                  locale === "en" ? enFont.className : arFont.className
                }
              >
                <Grid
                  container
                  spacing={{ xs: 2, md: 4 }}
                  sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: "20px",
                      }}
                    >
                      <Box
                        component="img"
                        alt={
                          selectedOffer?.merchantNameEn || selectedOffer?.title
                        }
                        src={selectedOffer?.img}
                        sx={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "7px",
                          objectFit: "cover",
                          display: "block",
                          padding: "0px !important",
                        }}
                      />
                      <Box sx={{ padding: "16px 24px" }}>
                        <Typography
                          sx={{
                            fontSize: "24px",
                            color: "#252C32",
                            fontWeight: 700,
                          }}
                        >
                          {selectedOffer?.title}
                        </Typography>

                        <Typography
                          sx={{
                            my: 1,
                            fontSize: 14,
                            color: colors.navy_blue,
                          }}
                        >
                          {selectedOffer?.role}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#686B7A",
                            padding: "4px 10px",
                            bgcolor: "#F8FAFC",
                            display: "inline",
                          }}
                        >
                          {offersText.valid_until}
                          {selectedOffer?.expired
                            ?.split(" ")[0]
                            .replaceAll("-", "/")}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          borderTop: "1px solid #D1D5DB",
                          padding: "24px",
                        }}
                      >
                        <ShareSocial
                          url={
                            typeof window !== "undefined"
                              ? window.location.href
                              : ""
                          }
                          showCopy={false}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        mb: "24px",
                        fontSize: "20px",
                        textAlign: "center",
                      }}
                    >
                      {offersText.to_know}
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: "20px",
                        p: 2,
                        border: "0.5px solid #D1D5DB",
                        bgcolor: "#fff",
                      }}
                    >
                      <OfferForm
                        product="shopping"
                        source={
                          process.env.NEXT_PUBLIC_MODE === "STAGING" ? 199 : 195
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ background: "white", mt: 5, py: 5 }}>
                  <OffersStepsSection
            t={t}
            locale={locale}
            isSmall={isSmall}
            isArabic={isArabic}
            steps={stepsWithNumber}
          />
                </Box>
              </DialogContent>
            </Dialog>
          </Box>
        )}
      </Box>

      {/* swiper of offers */}
      <Box sx={{ mt: 6 }}>
        <OffersStepsSection
            t={t}
            locale={locale}
            isSmall={isSmall}
            isArabic={isArabic}
            steps={stepsWithNumber}
          />
      </Box>
      {/**faq section */}
      <DynamicFAQSection
        title={offersText.faq?.title}
        items={offersText.faq?.items}
        renderHTML={true}
        variantTitle="h2"
        variantQ="h3"
      />
    </>
  );
}
